import { useState, useEffect } from 'react'

// Session-lived response cache keyed by URL. These endpoints are daily/nightly
// static (sparks, updates, news, …), so seeding a remount from the last
// response — then revalidating quietly — means re-entering a screen paints its
// sparklines/values instantly instead of flashing empty → data on every mount.
const FETCH_CACHE = new Map()

// Generic single-shot fetch hook (stale-while-revalidate on remount)
function useFetch(url) {
  const [data, setData] = useState(() => (FETCH_CACHE.has(url) ? FETCH_CACHE.get(url) : null))
  const [loading, setLoading] = useState(() => !FETCH_CACHE.has(url))
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    // A null/empty url means "not wanted yet" — used by lazy callers that only
    // fetch once a surface is opened. Without this the hook called fetch(null),
    // which requests "/null" and 404s. Purely additive: every existing caller
    // passes a real url and is unaffected.
    if (!url) { setLoading(false); return () => { cancelled = true } }
    // Serve cache immediately (no empty flash); else show loading.
    if (FETCH_CACHE.has(url)) { setData(FETCH_CACHE.get(url)); setLoading(false) }
    else setLoading(true)
    fetch(url)
      .then((r) => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then((d) => {
        if (cancelled) return
        FETCH_CACHE.set(url, d)
        setData(d)
        setLoading(false)
      })
      .catch((e) => {
        if (cancelled) return
        // Keep any cached data on a transient refresh failure.
        if (!FETCH_CACHE.has(url)) setError(String(e))
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [url])

  return { data, loading, error }
}

// Atlas Intel — long-form briefs / posts
export function useAtlasIntel() {
  const { data, loading, error } = useFetch('/api/atlas-intel')
  return { intel: Array.isArray(data) ? data : [], loading, error }
}

// Today's stock_updates (per-ticker AI analysis from nightly cron)
export function useStockUpdates() {
  const { data, loading, error } = useFetch('/api/updates-read')
  return { updates: Array.isArray(data) ? data : [], loading, error }
}

// Earnings calendar from Supabase
export function useEarnings() {
  const { data, loading, error } = useFetch('/api/atlas-data?resource=earnings')
  const rows = data?.earningsCalendar ?? (Array.isArray(data) ? data : [])
  return { earnings: rows, loading, error }
}

// Atlas equal-weight index — daily series for the Overview hero chart.
// series = chronological index_level array (drives AreaChart + starfield clip);
// latest = { date, level, pct } for the hero headline value + % change.
export function useAtlasIndex() {
  // limit=400 so the hero's 1Y window has enough daily rows once history accrues
  // (default cap is 120 ≈ 6 months). Payload is a few KB, edge-cached 5 min.
  const { data, loading, error } = useFetch('/api/atlas-index?limit=400')
  return {
    series: Array.isArray(data?.series) ? data.series : [],
    levels: Array.isArray(data?.levels) ? data.levels : [],
    latest: data?.latest ?? null,
    loading,
    error,
  }
}

// Per-sector daily index series for the Overview hero's comparison mode.
// Each series is already normalised to % from the window start server-side, so
// the sectors are directly comparable to each other and to the atlas_index line.
// Lazy by design: `enabled` stays false until the user switches to the sector
// view, so the default Overview doesn't pay for a chart nobody opened.
export function useSectorIndex(enabled, days = 365) {
  const { data, loading, error } = useFetch(enabled ? `/api/sector-index?days=${days}` : null)
  return {
    series: Array.isArray(data?.series) ? data.series : [],
    loading,
    error,
  }
}

// Pick of the Week — active pick + recent closed-pick track record
export function usePickOfWeek() {
  const { data, loading, error } = useFetch('/api/pick-of-week')
  return {
    active:  data?.active ?? null,
    history: data?.history ?? [],
    stats:   data?.stats ?? null,
    loading,
    error,
  }
}

// News articles for The Wire (news_articles table via /api/news, written by
// the fetch-news weekday cron). Returns rows newest-first.
export function useNews() {
  const { data, loading, error } = useFetch('/api/news')
  return { news: Array.isArray(data?.news) ? data.news : [], loading, error }
}

// Daily-close sparkline series, hard-cached ~daily. Returns a { ticker: number[] }
// map (oldest→newest closes); tickers without enough data are absent, so the
// Spark component falls back to its neutral flat line.
//
// No `range` → the fixed ~10 most recent sessions (Polygon grouped-daily) —
// used by Explore / Calendar / Insights. Pass a range (5D/1M/6M/1Y, or 1D for
// a short recent-daily trend) → the sparkline for THAT window, from the owned
// price_history table, for the whole universe in one call (audit F11). Each
// range is a distinct URL, so useFetch caches them independently.
export function useSparks(range) {
  const { data, loading } = useFetch(range ? `/api/sparks?range=${encodeURIComponent(range)}` : '/api/sparks')
  return {
    sparks: data?.sparks && typeof data.sparks === 'object' ? data.sparks : {},
    loading,
  }
}

// Open constellation_alerts (AI-clustered multi-signal alerts), sorted by score
export function useConstellationAlerts() {
  const { data, loading, error } = useFetch('/api/constellation-alerts')
  return { alerts: data?.alerts ?? [], loading, error }
}

// Today's signal_events (insider trades, 8-K filings, options unusual, etc.)
// since=today → from ET midnight onward
export function useSignalsToday() {
  const { data, loading, error } = useFetch('/api/signal-events?since=today&limit=200')
  return { events: data?.events ?? [], loading, error }
}

// Most-recent signal_events regardless of day — for the Overview "On the Wire"
// teaser, which should always show the latest few (not just today's, which is
// empty before the weekday signals cron runs).
export function useSignalsRecent(limit = 8) {
  const { data, loading, error } = useFetch(`/api/signal-events?limit=${limit}`)
  return { events: data?.events ?? [], loading, error }
}

